import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiX, FiCheck } from 'react-icons/fi';
import Pagination from '../utils/pagination2';

interface Option {
    value: string | number;
    label: string;
    description?: string;
    data?: any;
}

interface PaginationData {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
    has_next_page: boolean;
    has_prev_page: boolean;
    next_page: number | null;
    prev_page: number | null;
}

interface ModalSelectProps {
    label: string;
    placeholder?: string;
    value: string | number;
    onChange: (option: Option) => void;
    fetchOptions: (page: number, search?: string, limit?: number) => Promise<{
        data: Option[];
        pagination: PaginationData;
    }>;
    required?: boolean;
    error?: string;
    disabled?: boolean;
    searchPlaceholder?: string;
    loadingText?: string;
    noOptionsText?: string;
    emptyStateText?: string;
    debounceMs?: number;
    renderOption?: (option: Option) => React.ReactNode;
}

const ModalSelect: React.FC<ModalSelectProps> = ({
    label,
    placeholder = 'Select an option',
    value,
    onChange,
    fetchOptions,
    required = false,
    error,
    disabled = false,
    searchPlaceholder = 'Search...',
    loadingText = 'Loading...',
    noOptionsText = 'No options found',
    emptyStateText = 'No data available',
    debounceMs = 300,
    renderOption,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [options, setOptions] = useState<Option[]>([]);
    const [selectedOption, setSelectedOption] = useState<Option | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [pagination, setPagination] = useState<PaginationData | null>(null);
    const [loading, setLoading] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    // Load options
    const loadOptions = useCallback(async (page: number, search?: string, limit?: number) => {
        setLoading(true);
        try {
            const result = await fetchOptions(page, search, limit);
            setOptions(result.data);
            setPagination(result.pagination);
            return result;
        } catch (error) {
            console.error('Error loading options:', error);
            setOptions([]);
            setPagination(null);
            return { data: [], pagination: null };
        } finally {
            setLoading(false);
        }
    }, [fetchOptions]);

    // NEW: Auto-fetch initial data when value exists (for edit mode)
    useEffect(() => {
        if (value && !initialLoadDone && !isModalOpen) {
            loadOptions(1, '', perPage).then(() => {
                setInitialLoadDone(true);
            });
        }
    }, [value, initialLoadDone, isModalOpen, perPage]);

    // Handle search with debounce
    const handleSearch = (query: string) => {
        setSearchQuery(query);

        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            setCurrentPage(1);
            loadOptions(1, query, perPage);
        }, debounceMs);

        setSearchTimeout(timeout);
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        loadOptions(newPage, searchQuery, perPage);
    };

    // Handle limit change
    const handleLimitChange = (newLimit: number) => {
        setPerPage(newLimit);
        setCurrentPage(1);
        loadOptions(1, searchQuery, newLimit);
    };

    // Handle option selection
    const handleSelectOption = (option: Option) => {
        setSelectedOption(option);
        onChange(option);
        setIsModalOpen(false);
        setSearchQuery('');
        setCurrentPage(1);
    };

    // Open modal
    const openModal = () => {
        if (disabled) return;
        setIsModalOpen(true);
        loadOptions(1, '', perPage);
    };

    // Close modal
    const closeModal = () => {
        setIsModalOpen(false);
        setSearchQuery('');
        setCurrentPage(1);
    };

    // Find selected option label
    useEffect(() => {
        if (value && options.length > 0) {
            const found = options.find(opt => opt.value.toString() === value.toString());
            if (found) {
                setSelectedOption(found);
            }
        }
    }, [value, options]);

    // Cleanup timeout
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    return (
        <>
            {/* Trigger Button */}
            <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>

                <button
                    type="button"
                    onClick={openModal}
                    disabled={disabled}
                    className={`w-full px-4 py-2.5 text-left border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all ${error
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 hover:border-gray-400'
                        } ${disabled
                            ? 'bg-gray-100 cursor-not-allowed'
                            : 'bg-white cursor-pointer hover:bg-gray-50'
                        }`}
                >
                    <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
                        {selectedOption ? selectedOption.label : placeholder}
                    </span>
                </button>

                {error && (
                    <p className="mt-1 text-sm text-red-600">{error}</p>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">{label}</h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Search Bar */}
                        <div className="p-6 border-b border-gray-200">
                            <div className="relative">
                                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    placeholder={searchPlaceholder}
                                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* Options List */}
                        <div className="flex-1 overflow-y-auto p-6 min-h-[300px]">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                                    <p className="text-gray-600">{loadingText}</p>
                                </div>
                            ) : options.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                        <FiSearch className="w-8 h-8 text-gray-400" />
                                    </div>
                                    <p className="text-gray-600 text-center">
                                        {searchQuery ? noOptionsText : emptyStateText}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {options.map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => handleSelectOption(option)}
                                            className={`w-full px-4 py-3 rounded-lg text-left transition-all flex items-center justify-between group ${value.toString() === option.value.toString()
                                                ? 'bg-blue-50 border-2 border-primary-500'
                                                : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100 hover:border-gray-300'
                                                }`}
                                        >
                                            <div className="flex-1">
                                                {renderOption ? (
                                                    renderOption(option)
                                                ) : (
                                                    <>
                                                        <div className="font-medium text-gray-900">
                                                            {option.label}
                                                        </div>
                                                        {option.description && (
                                                            <div className="text-sm text-gray-500 mt-0.5">
                                                                {option.description}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            {value.toString() === option.value.toString() && (
                                                <FiCheck className="w-5 h-5 text-primary-600 flex-shrink-0 ml-3" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Pagination Footer */}
                        {pagination && pagination.total > 0 && (
                            <div className="border-t border-gray-200">
                                <Pagination
                                    pagination={pagination}
                                    onPageChange={handlePageChange}
                                    onLimitChange={handleLimitChange}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ModalSelect;